import { Container, Stack, Tab, tabClasses, Tabs, tabsClasses } from "@mui/material"
import { useStore } from "@nanostores/react"
import { motion } from "framer-motion"
import type React from "react"
import { useState } from "react"

import { useSearchParams } from "react-router-dom"

import { AgentStats } from "./AgentStats"
import { PoolsTable } from "./PoolsTable"
import { PortfolioAgentsTable } from "./PortfolioAgentsTable"
import { $allPools, $poolsOverviewMap } from "../../stores/pools-store"
import { AgentSvg } from "@/components/AgentSvg"
import { HeaderStatBar } from "@/components/RootLayout/HeaderStatBar"

type ActiveTab =
  | "pools"
  | "watchlist"
  | "agents-stats"
  | "agent-blueprints"
  | "old-agents"
  | "agent-index"

export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get("tab")

  const [activeTab, setActiveTab] = useState<ActiveTab>((tab as ActiveTab) || "pools")
  const handleChange = (event: React.SyntheticEvent | null, newValue: ActiveTab) => {
    setActiveTab(newValue)
    if (newValue === "pools") {
      setSearchParams({})
    } else {
      setSearchParams({ tab: newValue })
    }
  }

  const pools = useStore($allPools)
  const overviewMap = useStore($poolsOverviewMap)

  const supportedPools = pools
    .filter((x) => overviewMap && !!overviewMap[x.id])
    .filter((x) => x.status === "public")

  return (
    <Stack component={Container} disableGutters maxWidth="lg" sx={{ gap: { md: 3 } }}>
      <Stack
        sx={{
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
          gap: { xs: 3, sm: 0 },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleChange}
          sx={(theme) => ({
            [`& .${tabClasses.root}`]: {
              marginX: 0,
              fontSize: "1.5rem",
              fontWeight: `700 !important`,
              [theme.breakpoints.down("md")]: {
                fontSize: "1.275rem",
              },
            },
            [`& .${tabsClasses.indicator}`]: {
              background: "var(--mui-palette-accent-main) !important",
              height: 3,
            },
          })}
        >
          <Tab value="pools" label="Liquidity pools" />
          <Tab
            value="agent-index"
            label={
              <Stack direction="row" gap={1} alignItems="center">
                <AgentSvg height={20} width={24} />
                <span>Agent Index</span>
              </Stack>
            }
            component={motion.div}
            whileHover="hover"
            initial="rest"
            animate="rest"
          />
        </Tabs>
        <Stack direction="row" gap={2} alignContent="stretch">
          <HeaderStatBar />
          <AgentStats />
        </Stack>
      </Stack>
      {activeTab === "pools" && <PoolsTable pools={supportedPools} overviewMap={overviewMap} />}
      {activeTab === "agent-index" && <PortfolioAgentsTable />}
    </Stack>
  )
}
